import { useState, useEffect } from "react";
import api from "../../services/api";
import { Save, Eye, EyeOff, LayoutPanelLeft, Palette, UploadCloud, Image as ImageIcon, Gamepad2, Sparkles } from "lucide-react";
import toast, { Toaster } from "react-hot-toast";
import { useBranding } from "../../context/BrandingContext";

export default function InstituteSettings() {
  const [loading, setLoading] = useState(true);
  const [sidebarSettings, setSidebarSettings] = useState({
    student: {},
    teacher: {}
  });
  const [kidsDashboardEnabled, setKidsDashboardEnabled] = useState(false);


  const { refreshBranding } = useBranding();
  const [brandingForm, setBrandingForm] = useState({
    logo: "",
    instituteName: "",
    headerTitle: "",
    footerText: "",
    contactEmail: "",
    contactPhone: "",
    contactAddress: ""
  });
  
  const [landingConfigForm, setLandingConfigForm] = useState({
    heroTitle: "",
    heroSubtitle: "",
    heroTrustedBy: "",
    statsStudents: "",
    statsTeachers: "",
    statsCourses: "",
    statsResults: "",
    ctaTitle: "",
    ctaSubtitle: "",
    heroImage: "",
    liveClassImage: ""
  });

  const [apiKeysForm, setApiKeysForm] = useState({
    RAZORPAY_KEY: "",
    RAZORPAY_SECRET: "",
    ZOOM_ACCOUNT_ID: "",
    ZOOM_CLIENT_ID: "",
    ZOOM_CLIENT_SECRET: "",
    ZOOM_WEBHOOK_SECRET: "",
    SMTP_HOST: "",
    SMTP_PORT: "",
    SMTP_USER: "",
    SMTP_PASSWORD: "",
    YOUTUBE_API_KEY: ""
  });

  // These match the exact `label`s from Sidebar.js to facilitate global toggling
  const studentFeatures = [
    "Dashboard", "My Courses", "Continue Learning",
    "AI Tutor", "Recorded Videos", "Live Classes", "Notes & Resources",
    "Online Exams", "Homework & Tasks", "Coding Arena", "Ask Doubts",
    "Voice Tutor", "Daily Challenge", "Friend Compete", "Smart Revision",
    "Study Plan", "Performance", "Leaderboard", "Certificates",
    "Placement Preparation", "Internships & Jobs",
    "Subscription Plans", "Referral & Affiliate",
    "Notifications", "Calendar",
    "Profile & Settings", "Help & Support"
  ];

  const teacherFeatures = [
    "Teacher Dashboard", "My Courses", "Manage Exams", "Homework & Tasks",
    "Digital Library", "Coding Arena", "Doubt Desk", "Live Classes",
    "Broadcast Center"
  ];

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const [sidebarRes, brandingRes, kidsDashboardRes, apiKeysRes, landingConfigRes] = await Promise.all([
        api.get("/settings/sidebar_visibility"),
        api.get("/settings/branding").catch(() => ({ data: {} })),
        api.get("/settings/kids_dashboard").catch(() => ({ data: { enabled: false } })),
        api.get("/settings/api_keys").catch(() => ({ data: {} })),
        api.get("/settings/landing_page_config").catch(() => ({ data: {} }))
      ]);


      setSidebarSettings(sidebarRes.data || { student: {}, teacher: {} });
      setKidsDashboardEnabled(kidsDashboardRes.data?.enabled === true);
      if (brandingRes.data && typeof brandingRes.data === 'object') {
        setBrandingForm(prev => ({
          ...prev,
          ...brandingRes.data
        }));
      }
      if (apiKeysRes.data && typeof apiKeysRes.data === 'object') {
        setApiKeysForm(prev => ({
          ...prev,
          ...apiKeysRes.data
        }));
      }
      if (landingConfigRes.data && typeof landingConfigRes.data === 'object') {
        setLandingConfigForm(prev => ({
          ...prev,
          ...landingConfigRes.data
        }));
      }
    } catch (err) {
      toast.error("Failed to load system settings");
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e, fieldName) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    const toastId = toast.loading("Uploading image...");
    try {
      const res = await api.post("/upload/image", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setLandingConfigForm(prev => ({ ...prev, [fieldName]: res.data.url }));
      toast.success("Image uploaded successfully!", { id: toastId });
    } catch (err) {
      toast.error("Image upload failed.", { id: toastId });
    }
  };

  const handleBrandingImageUpload = async (e, fieldName) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    const toastId = toast.loading("Uploading image...");
    try {
      const res = await api.post("/upload/image", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setBrandingForm(prev => ({ ...prev, [fieldName]: res.data.url }));
      toast.success("Image uploaded successfully!", { id: toastId });
    } catch (err) {
      toast.error("Image upload failed.", { id: toastId });
    }
  };

  const handleToggle = (role, feature) => {
    setSidebarSettings(prev => {
      const isCurrentlyHidden = prev[role]?.[feature] === false;
      return {
        ...prev,
        [role]: {
          ...prev[role],
          [feature]: isCurrentlyHidden ? true : false // true means visible, false means hidden
        }
      };
    });
  };

  const saveSettings = async () => {
    try {
      await Promise.all([
        api.post("/settings/sidebar_visibility", { value: sidebarSettings }),
        api.post("/settings/branding", { value: brandingForm }),
        api.post("/settings/kids_dashboard", { value: { enabled: kidsDashboardEnabled } }),
        api.post("/settings/api_keys", { value: apiKeysForm }),
        api.post("/settings/landing_page_config", { value: landingConfigForm })
      ]);
      await refreshBranding(); // Broadcast changes globally
      toast.success("All configuration changes updated and live!");
    } catch (err) {
      toast.error("Save encountered errors, please retry.");
    }
  };

  const isVisible = (role, feature) => {
    // If not set, default is true
    return sidebarSettings[role]?.[feature] !== false;
  };

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <Toaster position="top-right" />
      <div className="surface overflow-hidden p-5 sm:p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <h1 className="page-title flex items-center gap-3">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600 dark:bg-indigo-950/60 dark:text-indigo-300">
                <LayoutPanelLeft size={22} />
              </span>
              <span className="min-w-0">Platform Sidebar Controls</span>
            </h1>
            <p className="page-subtitle">Globally toggle sidebar visibility for Student and Teacher portals.</p>
          </div>
          <button
            onClick={saveSettings}
            className="btn-primary w-full sm:w-auto"
          >
            <Save size={18} /> Save Configuration
          </button>
        </div>
      </div>

      {loading ? (
        <div className="surface flex justify-center p-10"><div className="h-8 w-8 animate-spin rounded-full border-4 border-indigo-600/20 border-t-indigo-600" /></div>
      ) : (
        <div className="space-y-8">
          {/* White Labeling Section */}
          <div className="surface p-6">
            <div className="mb-6 flex items-center gap-3 border-b border-slate-100 pb-4 dark:border-slate-800">
              <div className="rounded-xl bg-indigo-50 p-2 text-indigo-600 dark:bg-indigo-950 dark:text-indigo-400">
                <Palette size={20} />
              </div>
              <div>
                <h2 className="text-xl font-black text-slate-900 dark:text-white tracking-tight">White-Label & Identity</h2>
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Customize institute logos, names, and general display text.</p>
              </div>
            </div>

            <div className="grid gap-6 md:grid-cols-2">
              <div className="space-y-4">
                <div>
                  <label className="mb-1.5 block text-xs font-black uppercase tracking-widest text-slate-500 dark:text-slate-400">Institute Logo URL</label>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <input
                        type="text"
                        value={brandingForm.logo}
                        onChange={e => setBrandingForm({ ...brandingForm, logo: e.target.value })}
                        placeholder="Paste URL to .png, .svg, or .jpg"
                        className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 pl-11 text-sm font-medium text-slate-900 shadow-sm transition-all placeholder:text-slate-400 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-800 dark:bg-slate-900 dark:text-white dark:focus:border-indigo-600"
                      />
                      <ImageIcon className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                    </div>
                    <label className="cursor-pointer bg-slate-100 hover:bg-slate-200 px-3 py-2 rounded-xl text-sm font-bold border border-slate-200 dark:bg-slate-800 dark:border-slate-700 dark:text-white flex items-center shrink-0 transition-colors">
                      <UploadCloud size={16} className="mr-2" /> Upload
                      <input type="file" className="hidden" accept="image/*" onChange={(e) => handleBrandingImageUpload(e, 'logo')} />
                    </label>
                  </div>
                  <p className="mt-1.5 text-[10px] text-slate-500 italic">Leaves default if blank. Best size: 200px width height variable.</p>
                </div>

                <div>
                  <label className="mb-1.5 block text-xs font-black uppercase tracking-widest text-slate-500 dark:text-slate-400">Institute Name</label>
                  <input
                    type="text"
                    value={brandingForm.instituteName}
                    onChange={e => setBrandingForm({ ...brandingForm, instituteName: e.target.value })}
                    placeholder="E.g. Stanford Academy"
                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-900 shadow-sm dark:border-slate-800 dark:bg-slate-900 dark:text-white"
                  />
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="mb-1.5 block text-xs font-black uppercase tracking-widest text-slate-500 dark:text-slate-400">Browser Tab Title / Header</label>
                  <input
                    type="text"
                    value={brandingForm.headerTitle}
                    onChange={e => setBrandingForm({ ...brandingForm, headerTitle: e.target.value })}
                    placeholder="E.g. Academy LMS"
                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-900 shadow-sm dark:border-slate-800 dark:bg-slate-900 dark:text-white"
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-xs font-black uppercase tracking-widest text-slate-500 dark:text-slate-400">System Footer Text</label>
                  <input
                    type="text"
                    value={brandingForm.footerText}
                    onChange={e => setBrandingForm({ ...brandingForm, footerText: e.target.value })}
                    placeholder="© 2026 Your Name. All Rights Reserved."
                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-900 shadow-sm dark:border-slate-800 dark:bg-slate-900 dark:text-white"
                  />
                </div>
              </div>
            </div>

            <div className="grid gap-6 md:grid-cols-3 mt-6 pt-6 border-t border-slate-100 dark:border-slate-800">
              <div>
                <label className="mb-1.5 block text-xs font-black uppercase tracking-widest text-slate-500 dark:text-slate-400">Contact Email</label>
                <input
                  type="email"
                  value={brandingForm.contactEmail || ""}
                  onChange={e => setBrandingForm({ ...brandingForm, contactEmail: e.target.value })}
                  placeholder="support@institute.com"
                  className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-900 shadow-sm dark:border-slate-800 dark:bg-slate-900 dark:text-white"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-black uppercase tracking-widest text-slate-500 dark:text-slate-400">Contact Phone</label>
                <input
                  type="text"
                  value={brandingForm.contactPhone || ""}
                  onChange={e => setBrandingForm({ ...brandingForm, contactPhone: e.target.value })}
                  placeholder="+1 234 567 8900"
                  className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-900 shadow-sm dark:border-slate-800 dark:bg-slate-900 dark:text-white"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-black uppercase tracking-widest text-slate-500 dark:text-slate-400">Contact Address</label>
                <input
                  type="text"
                  value={brandingForm.contactAddress || ""}
                  onChange={e => setBrandingForm({ ...brandingForm, contactAddress: e.target.value })}
                  placeholder="123 Education Hub..."
                  className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-900 shadow-sm dark:border-slate-800 dark:bg-slate-900 dark:text-white"
                />
              </div>
            </div>

            {brandingForm.logo && (
              <div className="mt-6 rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900/50">
                <p className="mb-2 text-[10px] font-black uppercase tracking-widest text-slate-400">Logo Preview</p>
                <div className="flex h-16 items-center justify-center overflow-hidden rounded-lg bg-white dark:bg-slate-800">
                  <img src={brandingForm.logo} alt="Brand logo preview" className="h-12 object-contain" onError={(e) => e.target.style.display = 'none'} />
                </div>
              </div>
            )}
          </div>
          
          {/* Global Experience Configuration */}
          <div className="surface p-6 overflow-hidden relative group">
            {/* Accent background pattern */}
            <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-yellow-400/10 blur-3xl group-hover:bg-yellow-400/20 transition-all" />
            <div className="absolute -left-10 -bottom-10 h-40 w-40 rounded-full bg-purple-500/10 blur-3xl group-hover:bg-purple-500/20 transition-all" />

            <div className="relative z-10 flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
              <div className="flex items-start gap-4">
                <div className="rounded-2xl bg-gradient-to-br from-yellow-400 to-orange-500 p-3 text-white shadow-lg shadow-orange-500/20 shrink-0 animate-pulse">
                  <Gamepad2 size={24} />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-xl font-black text-slate-900 dark:text-white tracking-tight">Global Student Experience</h2>
                    <span className="px-2.5 py-0.5 rounded-full bg-indigo-50 dark:bg-indigo-950 text-[10px] font-black text-indigo-600 dark:text-indigo-400 uppercase tracking-widest flex items-center gap-1 border dark:border-indigo-900">
                      <Sparkles size={10} fill="currentColor" /> Gamification Enabled
                    </span>
                  </div>
                  <p className="text-slate-500 mt-1.5 text-sm font-semibold max-w-2xl leading-relaxed">
                    Toggle the immersive, vibrant, and gamified dashboard experience designed explicitly for junior scholars. When enabled, standard students view Level Trackers, Badges, & Cartoon Buddies instead of standard analytics.
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-4 self-start shrink-0 bg-slate-50 dark:bg-slate-900 p-4 rounded-2xl border dark:border-slate-800 md:self-center">
                <span className={`text-sm font-black tracking-tight ${kidsDashboardEnabled ? 'text-emerald-600' : 'text-slate-400'}`}>
                  {kidsDashboardEnabled ? 'KIDS DASHBOARD ACTIVE 🎮' : 'DISABLED'}
                </span>
                <button
                  type="button"
                  onClick={() => setKidsDashboardEnabled(!kidsDashboardEnabled)}
                  className={`relative inline-flex h-7 w-14 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${kidsDashboardEnabled ? 'bg-emerald-500' : 'bg-slate-200 dark:bg-slate-700'}`}
                >
                  <span
                    aria-hidden="true"
                    className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${kidsDashboardEnabled ? 'translate-x-7' : 'translate-x-0'}`}
                  />
                </button>
              </div>
            </div>
          </div>

          {/* Landing Page Configuration */}
          <div className="surface p-6">
            <div className="mb-6 flex items-center gap-3 border-b border-slate-100 pb-4 dark:border-slate-800">
              <div className="rounded-xl bg-purple-50 p-2 text-purple-600 dark:bg-purple-950 dark:text-purple-400">
                <LayoutPanelLeft size={20} />
              </div>
              <div>
                <h2 className="text-xl font-black text-slate-900 dark:text-white tracking-tight">Landing Page Content</h2>
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Customize headlines and stats shown on the public home page.</p>
              </div>
            </div>

            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 mb-8">
              <div className="space-y-4 rounded-xl border border-slate-200 p-4 dark:border-slate-800 lg:col-span-3">
                <h3 className="text-sm font-bold text-slate-900 dark:text-white uppercase tracking-widest border-b pb-2 mb-4 dark:border-slate-800">Hero Section</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="mb-1.5 block text-xs font-black uppercase tracking-widest text-slate-500">Hero Headline</label>
                    <input type="text" value={landingConfigForm.heroTitle} onChange={e => setLandingConfigForm({ ...landingConfigForm, heroTitle: e.target.value })} placeholder="Default Title" className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-800 dark:bg-slate-900 dark:text-white" />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-xs font-black uppercase tracking-widest text-slate-500">Hero Subheadline</label>
                    <input type="text" value={landingConfigForm.heroSubtitle} onChange={e => setLandingConfigForm({ ...landingConfigForm, heroSubtitle: e.target.value })} placeholder="Default Subtitle" className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-800 dark:bg-slate-900 dark:text-white" />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-xs font-black uppercase tracking-widest text-slate-500">Trusted By Text</label>
                    <input type="text" value={landingConfigForm.heroTrustedBy} onChange={e => setLandingConfigForm({ ...landingConfigForm, heroTrustedBy: e.target.value })} placeholder="Default: 5000+ Students" className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-800 dark:bg-slate-900 dark:text-white" />
                  </div>
                </div>
              </div>

              <div className="space-y-4 rounded-xl border border-slate-200 p-4 dark:border-slate-800 lg:col-span-3">
                <h3 className="text-sm font-bold text-slate-900 dark:text-white uppercase tracking-widest border-b pb-2 mb-4 dark:border-slate-800">Institute Statistics</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <label className="mb-1.5 block text-xs font-black uppercase tracking-widest text-slate-500">Students Count</label>
                    <input type="text" value={landingConfigForm.statsStudents} onChange={e => setLandingConfigForm({ ...landingConfigForm, statsStudents: e.target.value })} placeholder="Default: 10k+" className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-800 dark:bg-slate-900 dark:text-white" />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-xs font-black uppercase tracking-widest text-slate-500">Teachers Count</label>
                    <input type="text" value={landingConfigForm.statsTeachers} onChange={e => setLandingConfigForm({ ...landingConfigForm, statsTeachers: e.target.value })} placeholder="Default: 150+" className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-800 dark:bg-slate-900 dark:text-white" />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-xs font-black uppercase tracking-widest text-slate-500">Courses Count</label>
                    <input type="text" value={landingConfigForm.statsCourses} onChange={e => setLandingConfigForm({ ...landingConfigForm, statsCourses: e.target.value })} placeholder="Default: 200+" className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-800 dark:bg-slate-900 dark:text-white" />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-xs font-black uppercase tracking-widest text-slate-500">Results %</label>
                    <input type="text" value={landingConfigForm.statsResults} onChange={e => setLandingConfigForm({ ...landingConfigForm, statsResults: e.target.value })} placeholder="Default: 95%" className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-800 dark:bg-slate-900 dark:text-white" />
                  </div>
                </div>
              </div>

              <div className="space-y-4 rounded-xl border border-slate-200 p-4 dark:border-slate-800 lg:col-span-3">
                <h3 className="text-sm font-bold text-slate-900 dark:text-white uppercase tracking-widest border-b pb-2 mb-4 dark:border-slate-800">Call To Action (CTA)</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="mb-1.5 block text-xs font-black uppercase tracking-widest text-slate-500">CTA Headline</label>
                    <input type="text" value={landingConfigForm.ctaTitle} onChange={e => setLandingConfigForm({ ...landingConfigForm, ctaTitle: e.target.value })} placeholder="Default: Start Your Learning Journey Today" className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-800 dark:bg-slate-900 dark:text-white" />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-xs font-black uppercase tracking-widest text-slate-500">CTA Subheadline</label>
                    <input type="text" value={landingConfigForm.ctaSubtitle} onChange={e => setLandingConfigForm({ ...landingConfigForm, ctaSubtitle: e.target.value })} placeholder="Default: Join 10,000+ students..." className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-800 dark:bg-slate-900 dark:text-white" />
                  </div>
                </div>
              </div>
              <div className="space-y-4 rounded-xl border border-slate-200 p-4 dark:border-slate-800 lg:col-span-3">
                <h3 className="text-sm font-bold text-slate-900 dark:text-white uppercase tracking-widest border-b pb-2 mb-4 dark:border-slate-800">Media & Images</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="mb-1.5 block text-xs font-black uppercase tracking-widest text-slate-500">Hero Section Image</label>
                    <div className="flex gap-2">
                      <input type="text" value={landingConfigForm.heroImage} onChange={e => setLandingConfigForm({ ...landingConfigForm, heroImage: e.target.value })} placeholder="Image URL" className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-800 dark:bg-slate-900 dark:text-white" />
                      <label className="cursor-pointer bg-slate-100 hover:bg-slate-200 px-3 py-2 rounded-lg text-sm font-bold border border-slate-200 dark:bg-slate-800 dark:border-slate-700 dark:text-white flex items-center shrink-0 transition-colors">
                        <UploadCloud size={16} className="mr-2" /> Upload
                        <input type="file" className="hidden" accept="image/*" onChange={(e) => handleImageUpload(e, 'heroImage')} />
                      </label>
                    </div>
                  </div>
                  <div>
                    <label className="mb-1.5 block text-xs font-black uppercase tracking-widest text-slate-500">Live Classes Image</label>
                    <div className="flex gap-2">
                      <input type="text" value={landingConfigForm.liveClassImage} onChange={e => setLandingConfigForm({ ...landingConfigForm, liveClassImage: e.target.value })} placeholder="Image URL" className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-800 dark:bg-slate-900 dark:text-white" />
                      <label className="cursor-pointer bg-slate-100 hover:bg-slate-200 px-3 py-2 rounded-lg text-sm font-bold border border-slate-200 dark:bg-slate-800 dark:border-slate-700 dark:text-white flex items-center shrink-0 transition-colors">
                        <UploadCloud size={16} className="mr-2" /> Upload
                        <input type="file" className="hidden" accept="image/*" onChange={(e) => handleImageUpload(e, 'liveClassImage')} />
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* API Keys Configuration removed from Institute Settings */}

          <div className="grid gap-6 lg:grid-cols-2">
            {/* Student Panel */}
            <div className="surface p-4 sm:p-6">
              <div className="mb-4 border-b border-slate-200 pb-3 dark:border-slate-800">
                <h2 className="text-lg font-black text-slate-900 dark:text-white">Student Portal</h2>
                <p className="text-sm text-slate-500 dark:text-slate-400">{studentFeatures.length} navigation items</p>
              </div>
              <div className="space-y-2">
                {studentFeatures.map(feature => (
                  <div key={feature} className="flex flex-col gap-3 rounded-lg bg-slate-50 p-3 transition-colors hover:bg-slate-100 dark:bg-slate-950/60 dark:hover:bg-slate-800 sm:flex-row sm:items-center sm:justify-between">
                    <span className="text-sm font-bold text-slate-700 dark:text-slate-200">{feature}</span>
                    <button
                      onClick={() => handleToggle("student", feature)}
                      className={`inline-flex w-full items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-black transition-colors sm:w-auto ${isVisible("student", feature)
                        ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200 dark:bg-emerald-950/60 dark:text-emerald-300'
                        : 'bg-slate-200 text-slate-500 hover:bg-slate-300 dark:bg-slate-800 dark:text-slate-400'
                        }`}
                    >
                      {isVisible("student", feature) ? <><Eye size={14} /> Visible</> : <><EyeOff size={14} /> Hidden</>}
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Teacher Panel */}
            <div className="surface p-4 sm:p-6">
              <div className="mb-4 border-b border-slate-200 pb-3 dark:border-slate-800">
                <h2 className="text-lg font-black text-slate-900 dark:text-white">Teacher Portal</h2>
                <p className="text-sm text-slate-500 dark:text-slate-400">{teacherFeatures.length} navigation items</p>
              </div>
              <div className="space-y-2">
                {teacherFeatures.map(feature => (
                  <div key={feature} className="flex flex-col gap-3 rounded-lg bg-slate-50 p-3 transition-colors hover:bg-slate-100 dark:bg-slate-950/60 dark:hover:bg-slate-800 sm:flex-row sm:items-center sm:justify-between">
                    <span className="text-sm font-bold text-slate-700 dark:text-slate-200">{feature}</span>
                    <button
                      onClick={() => handleToggle("teacher", feature)}
                      className={`inline-flex w-full items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-black transition-colors sm:w-auto ${isVisible("teacher", feature)
                        ? 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200 dark:bg-indigo-950/60 dark:text-indigo-300'
                        : 'bg-slate-200 text-slate-500 hover:bg-slate-300 dark:bg-slate-800 dark:text-slate-400'
                        }`}
                    >
                      {isVisible("teacher", feature) ? <><Eye size={14} /> Visible</> : <><EyeOff size={14} /> Hidden</>}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
